/**
 * Admin Classes/Schedule Management - Olympic Fit Wellness
 * Simplified: Just schedule sessions that members can book
 */
import { useState, useEffect } from 'react';
import { 
  Calendar, 
  Plus,
  Search,
  Trash2,
  Clock,
  Users,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import api from '../../lib/api';

// Session type colors
const SESSION_TYPE_STYLES = {
  group_class: { bg: 'bg-blue-500/20 text-blue-400', label: 'Group Class' },
  one_on_one: { bg: 'bg-green-500/20 text-green-400', label: '1-on-1 Session' },
  workshop: { bg: 'bg-purple-500/20 text-purple-400', label: 'Workshop' }
};

export default function ClassesAdminPage() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [sessionForm, setSessionForm] = useState({
    title: '',
    description: '',
    session_type: 'group_class',
    service_category: 'group_class',
    start_time: '',
    end_time: '',
    duration_minutes: 45,
    max_participants: 20,
    price: 0,
    included_in_membership: true
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const data = await api.getCalendarSessions({});
      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session =>
    session.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    session.staff_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle time change and auto-calculate duration
  const handleTimeChange = (field, value) => {
    const newForm = { ...sessionForm, [field]: value };
    if (newForm.start_time && newForm.end_time) {
      const start = new Date(newForm.start_time);
      const end = new Date(newForm.end_time);
      newForm.duration_minutes = Math.round((end - start) / 60000);
    }
    setSessionForm(newForm);
  };

  // Create session - immediately visible to members, NO approval needed
  const handleCreateSession = async () => {
    if (!sessionForm.title || !sessionForm.start_time || !sessionForm.end_time) {
      toast.error('Please fill in Title, Start Time, and End Time');
      return;
    }

    if (new Date(sessionForm.end_time) <= new Date(sessionForm.start_time)) {
      toast.error('End time must be after start time');
      return;
    }

    setFormLoading(true);
    try {
      const result = await api.createCalendarSession(sessionForm);
      if (result.success) {
        toast.success('Session created! Members can now book it.');
        setShowCreateModal(false);
        resetForm();
        loadSessions();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create session');
    } finally {
      setFormLoading(false);
    }
  };

  // Cancel session
  const handleCancelSession = async (sessionId, sessionTitle) => {
    if (!confirm(`Cancel "${sessionTitle}"? All bookings will be cancelled and members notified.`)) return;
    
    try {
      await api.cancelCalendarSession(sessionId, 'Cancelled by admin');
      toast.success('Session cancelled');
      loadSessions();
    } catch (error) {
      toast.error(error.message || 'Failed to cancel session');
    }
  };

  const resetForm = () => {
    setSessionForm({
      title: '',
      description: '',
      session_type: 'group_class',
      service_category: 'group_class',
      start_time: '',
      end_time: '',
      duration_minutes: 45,
      max_participants: 20,
      price: 0,
      included_in_membership: true
    });
  };

  // Get default datetime (tomorrow at 9am)
  const getDefaultDateTime = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return tomorrow.toISOString().slice(0, 16);
  };

  const openCreateModal = () => {
    const startDefault = getDefaultDateTime();
    const endDefault = new Date(new Date(startDefault).getTime() + 60 * 60 * 1000).toISOString().slice(0, 16);
    setSessionForm({
      ...sessionForm,
      start_time: startDefault,
      end_time: endDefault,
      duration_minutes: 60
    });
    setShowCreateModal(true);
  };

  return (
    <div className="space-y-6" data-testid="classes-admin">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Schedule Classes</h1>
          <p className="text-neutral-400">Create sessions that members can book immediately</p>
        </div>
        <button 
          onClick={openCreateModal}
          className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] text-white font-semibold px-6 py-3 text-sm tracking-wider hover:bg-blue-600 transition-colors"
          data-testid="create-session-btn"
        >
          <Plus className="w-4 h-4" />
          CREATE SESSION
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-blue-400 text-sm">
          <strong>How it works:</strong> Sessions you create here appear immediately on the member schedule. 
          Members can book group classes instantly. For 1-on-1 sessions, members send a request that you can approve/decline.
        </p>
      </div>

      {/* Search */}
      <div className="bg-neutral-800 rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
          <input
            type="text"
            placeholder="Search sessions by title or instructor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-3 bg-neutral-700 border border-neutral-600 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:border-[#3B82F6]"
          />
        </div>
      </div>

      {/* Sessions Table */}
      <div className="bg-neutral-800 rounded-xl border border-neutral-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Session</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Type</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Instructor</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Date & Time</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Bookings</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-neutral-400 uppercase">Status</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-neutral-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-700">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin mx-auto" />
                </td>
              </tr>
            ) : filteredSessions.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-neutral-400">
                  {searchQuery ? 'No sessions match your search' : 'No sessions scheduled. Click "CREATE SESSION" to add one.'}
                </td>
              </tr>
            ) : filteredSessions.slice(0, 50).map((session) => {
              const typeStyle = SESSION_TYPE_STYLES[session.session_type] || SESSION_TYPE_STYLES.group_class;
              const isFull = session.current_participants >= session.max_participants;
              
              return (
                <tr key={session.id} className="hover:bg-neutral-700/50" data-testid={`session-row-${session.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-white">{session.title}</div>
                    {session.description && (
                      <div className="text-xs text-neutral-500 truncate max-w-xs">{session.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${typeStyle.bg}`}>
                      {typeStyle.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-neutral-400">{session.staff_name || 'Staff'}</td>
                  <td className="px-6 py-4 text-neutral-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {session.start && new Date(session.start).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Clock className="w-3 h-3" />
                      {session.start && new Date(session.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {' - '}
                      {session.end && new Date(session.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-neutral-400" />
                      <span className={`${isFull ? 'text-red-400' : 'text-white'}`}>
                        {session.current_participants || 0}/{session.max_participants}
                      </span>
                      {isFull && <span className="text-xs text-red-400">(Full)</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      session.status === 'scheduled' ? 'bg-green-500/20 text-green-400' :
                      session.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                      'bg-neutral-600 text-neutral-400'
                    }`}>
                      {session.status === 'scheduled' ? 'Active' : session.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => window.open(`/portal/schedule`, '_blank')}
                        className="p-2 hover:bg-neutral-600 rounded-lg"
                        title="View on member schedule"
                      >
                        <Eye className="w-4 h-4 text-neutral-400" />
                      </button>
                      {session.status === 'scheduled' && (
                        <button 
                          onClick={() => handleCancelSession(session.id, session.title)}
                          className="p-2 hover:bg-red-500/20 rounded-lg"
                          title="Cancel session"
                          data-testid={`cancel-session-${session.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredSessions.length > 50 && (
          <div className="px-6 py-4 bg-neutral-700/30 text-center text-neutral-400 text-sm">
            Showing 50 of {filteredSessions.length} sessions. Use search to filter.
          </div>
        )}
      </div>

      {/* Create Session Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg bg-neutral-800 text-white border-neutral-700">
          <DialogHeader>
            <DialogTitle>Create New Session</DialogTitle>
            <DialogDescription className="text-neutral-400">
              This session will be immediately available for members to book
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Session Title *</Label>
              <Input
                value={sessionForm.title}
                onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })}
                placeholder="e.g., Morning Yoga, Power HIIT, Personal Training"
                className="bg-neutral-700 border-neutral-600 text-white"
                data-testid="session-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Session Type</Label>
                <Select
                  value={sessionForm.session_type}
                  onValueChange={(value) => setSessionForm({ ...sessionForm, session_type: value })}
                >
                  <SelectTrigger className="bg-neutral-700 border-neutral-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="group_class">Group Class (instant booking)</SelectItem>
                    <SelectItem value="one_on_one">1-on-1 Session (request approval)</SelectItem>
                    <SelectItem value="workshop">Workshop (instant booking)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white">Max Participants</Label>
                <Input
                  type="number"
                  min="1"
                  value={sessionForm.max_participants}
                  onChange={(e) => setSessionForm({ ...sessionForm, max_participants: parseInt(e.target.value) || 1 })}
                  className="bg-neutral-700 border-neutral-600 text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Start Time *</Label>
                <Input
                  type="datetime-local"
                  value={sessionForm.start_time}
                  onChange={(e) => handleTimeChange('start_time', e.target.value)}
                  className="bg-neutral-700 border-neutral-600 text-white"
                  data-testid="session-start-time"
                />
              </div>

              <div>
                <Label className="text-white">End Time *</Label>
                <Input
                  type="datetime-local"
                  value={sessionForm.end_time}
                  onChange={(e) => handleTimeChange('end_time', e.target.value)}
                  className="bg-neutral-700 border-neutral-600 text-white"
                  data-testid="session-end-time"
                />
              </div>
            </div>

            {sessionForm.duration_minutes > 0 && (
              <p className="text-sm text-neutral-400">
                Duration: {sessionForm.duration_minutes} minutes
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={sessionForm.price}
                  onChange={(e) => setSessionForm({ ...sessionForm, price: parseFloat(e.target.value) || 0 })}
                  className="bg-neutral-700 border-neutral-600 text-white"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sessionForm.included_in_membership}
                    onChange={(e) => setSessionForm({ ...sessionForm, included_in_membership: e.target.checked })}
                    className="rounded border-neutral-600"
                  />
                  Free for members
                </label>
              </div>
            </div>

            <div>
              <Label className="text-white">Description (optional)</Label>
              <Textarea
                value={sessionForm.description}
                onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })}
                placeholder="What will participants learn or do in this session?"
                className="bg-neutral-700 border-neutral-600 text-white"
                rows={3}
              />
            </div>

            {/* Booking Flow Explanation */}
            <div className="bg-neutral-700/50 rounded-lg p-3 text-sm">
              <p className="text-neutral-300 font-medium mb-1">After creation:</p>
              {sessionForm.session_type === 'one_on_one' ? (
                <p className="text-neutral-400">
                  Members will see this on the schedule and can <strong>request</strong> to book. 
                  You'll receive a notification to approve or decline.
                </p>
              ) : (
                <p className="text-neutral-400">
                  Members will see this on the schedule and can <strong>book instantly</strong>. 
                  No approval needed.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSession}
              disabled={formLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-session-submit"
            >
              {formLoading ? 'Creating...' : 'Create Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
